/**
 * Types and constants for PO Lines Tab components
 */
import { PurchaseOrderLine } from '../../../../lib/api';

// CR-3406: Column configuration for visibility management
// CR-3846: Added order field for column reordering
export interface ColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  sortable?: boolean;
  minWidth: string;
  order: number;  // CR-3846: Column display order
}

// CR-3850: Added additional columns from old platform
export const DEFAULT_COLUMNS: ColumnConfig[] = [
  { id: 'processing_stage', label: 'Status', visible: true, sortable: true, minWidth: '130px', order: 0 },
  { id: 'acknowledge_status', label: 'Ack Status', visible: false, sortable: true, minWidth: '170px', order: 0 },
  { id: 'hold_reason', label: 'Hold Reason', visible: false, sortable: false, minWidth: '260px', order: 1 },
  { id: 'customer_name', label: 'Customer', visible: true, sortable: true, minWidth: '220px', order: 2 },
  { id: 'po_number', label: 'SO Number', visible: true, sortable: true, minWidth: '140px', order: 3 },
  { id: 'order_date', label: 'Order Date', visible: true, sortable: true, minWidth: '120px', order: 4 },
  { id: 'line_number', label: 'Line #', visible: true, sortable: true, minWidth: '60px', order: 5 },
  { id: 'part_number', label: 'Part Number', visible: true, sortable: true, minWidth: '160px', order: 6 },
  { id: 'description', label: 'Description', visible: true, sortable: true, minWidth: '200px', order: 7 },
  { id: 'supplier_promise_date', label: 'Supplier Promise Date', visible: true, sortable: true, minWidth: '200px', order: 8 },
  { id: 'outstanding_qty', label: 'Qty O/S', visible: true, sortable: true, minWidth: '140px', order: 9 },
  { id: 'priority', label: 'Priority', visible: true, sortable: true, minWidth: '100px', order: 10 },
  { id: 'action', label: 'Action', visible: false, sortable: false, minWidth: '100px', order: 11 },
  { id: 'is_critical', label: 'Critical', visible: false, sortable: true, minWidth: '80px', order: 12 },
  { id: 'on_hold', label: 'On Hold', visible: false, sortable: true, minWidth: '100px', order: 13 },
  { id: 'next_action', label: 'Next Action', visible: false, sortable: true, minWidth: '120px', order: 14 },
  { id: 'supplier_name', label: 'Supplier', visible: false, sortable: true, minWidth: '150px', order: 15 },
  { id: 'customer_group_name', label: 'Customer Group', visible: false, sortable: true, minWidth: '150px', order: 16 },  // CR-3850
  { id: 'quantity', label: 'Qty Ordered', visible: false, sortable: true, minWidth: '140px', order: 17 },
  { id: 'received_qty', label: 'Qty Delivered', visible: false, sortable: true, minWidth: '160px', order: 18 },
  { id: 'pct_delivered', label: '% Delivered', visible: false, sortable: true, minWidth: '130px', order: 19 },
  { id: 'unit_price', label: 'Unit Price', visible: false, sortable: true, minWidth: '140px', order: 20 },
  { id: 'total_price', label: 'Total', visible: false, sortable: true, minWidth: '110px', order: 21 },
  { id: 'currency', label: 'Currency', visible: false, sortable: true, minWidth: '100px', order: 22 },
  // Note: supplier-portal preserves the delivery_date column option (hidden by
  // default). Upstream removed it as part of CR-4694 round 2 rework, but our
  // downstream views still surface it.
  { id: 'delivery_date', label: 'Delivery Date', visible: false, sortable: true, minWidth: '200px', order: 23 },
  { id: 'buyer_id', label: 'Buyer ID', visible: false, sortable: true, minWidth: '100px', order: 24 },
  { id: 'batch_serial_no', label: 'Batch/Serial No', visible: false, sortable: true, minWidth: '140px', order: 25 },
  { id: 'ppv', label: 'PPV', visible: false, sortable: true, minWidth: '100px', order: 26 },
  { id: 'otif_overridden', label: 'OTIF Overridden', visible: false, sortable: true, minWidth: '130px', order: 27 },
  { id: 'otd_overridden', label: 'OTD Overridden', visible: false, sortable: true, minWidth: '130px', order: 27 },  // CR-5263
  { id: 'poeton_job_number', label: 'Job No(s)', visible: false, sortable: true, minWidth: '130px', order: 27 },  // CR-5260
  { id: 'job_status', label: 'Job Status', visible: false, sortable: false, minWidth: '140px', order: 27 },
  { id: 'job_no_active', label: 'Job #', visible: false, sortable: false, minWidth: '100px', order: 28 },
  { id: 'job_qty_active', label: 'Job Qty', visible: false, sortable: false, minWidth: '80px', order: 29 },
  { id: 'packing_note_number', label: 'PnR Note No', visible: false, sortable: true, minWidth: '120px', order: 28 },
  { id: 'last_received_date', label: 'Last Received', visible: false, sortable: true, minWidth: '120px', order: 29 },
  { id: 'updated_at', label: 'Update Time', visible: false, sortable: true, minWidth: '150px', order: 30 },
  { id: 'required_date_change_count', label: 'Req Date Changes', visible: false, sortable: true, minWidth: '130px', order: 31 },
  { id: 'promised_date_change_count', label: 'Promise Date Changes', visible: false, sortable: true, minWidth: '150px', order: 32 },
  { id: 'pdf_attached_count', label: 'PDF Attached', visible: false, sortable: true, minWidth: '100px', order: 33 },
  { id: 'material_cert_count', label: 'Mat Certs', visible: false, sortable: true, minWidth: '100px', order: 34 },
  { id: 'contract_unit_price', label: 'Contract Unit Price', visible: false, sortable: true, minWidth: '140px', order: 35 },
  { id: 'contract_lot_price', label: 'Contract Lot Price', visible: false, sortable: true, minWidth: '140px', order: 36 },
  { id: 'contract_price', label: 'Contract Price', visible: false, sortable: true, minWidth: '130px', order: 37 },
  { id: 'drawing_number', label: 'Drawing No', visible: false, sortable: true, minWidth: '120px', order: 38 },
  { id: 'drawing_version', label: 'Drawing Issue', visible: false, sortable: true, minWidth: '100px', order: 39 },
  { id: 'outstanding_value', label: 'O/S Value', visible: false, sortable: true, minWidth: '120px', order: 40 },
  { id: 'converted_unit_price', label: 'Conv. Unit Price', visible: false, sortable: true, minWidth: '130px', order: 41 },
  { id: 'converted_total_value', label: 'Conv. Total', visible: false, sortable: true, minWidth: '120px', order: 42 },
  { id: 'converted_outstanding_value', label: 'Conv. O/S Value', visible: false, sortable: true, minWidth: '130px', order: 43 },
  { id: 'original_promise_date', label: 'Agreed Date (ODD)', visible: false, sortable: true, minWidth: '140px', order: 44 },
  { id: 'mrp_scheduled_date', label: 'MRP/ERP Scheduled', visible: false, sortable: true, minWidth: '150px', order: 45 },
  { id: 'exchange_rate', label: 'Exchange Rate', visible: false, sortable: true, minWidth: '120px', order: 46 },  // CR-4679
];

export const COLUMN_STORAGE_KEY = 'po_lines_column_config';
export const COLUMN_VERSION_STORAGE_KEY = 'po_lines_column_config_version';

// Bump this when DEFAULT_COLUMNS changes shape/intent enough that we
// want to force-reset every user's saved layout (localStorage + server).
// On load, any saved config persisted with a lower version is discarded
// and DEFAULT_COLUMNS takes over.
// Bumped to 4 to surface the split Job #/Job Qty columns alongside
// the existing Job Status pill on existing user layouts.
// Bumped to 5 to restore the Ack Status column dropped during the
// upstream/development sync (2026-05-04).
// Bumped to 6 to align defaults with source-of-truth Open Orders report
// (2026-05-05): show Batch/Serial No by default, hide the cryptic Job #
// and Job Qty columns. Job Status pill stays visible for the Poeton
// progress feed.
// Bumped to 7 to hide Hold Reason by default — for Cardiff the column was
// 260px of dashes on every non-on-hold row (most of them); the Ack Status
// badge already surfaces "On Hold" when it matters. Users can still toggle
// Hold Reason from the column picker.
// Bumped to 8 to hide Job Status by default — Cardiff has progress text
// for only ~half of lines and the rest render '-'; the trailing 'View →'
// link column also removed (entire row is clickable).
// Bumped to 9 to RESTORE Job Status, Job #, Job Qty as visible by default —
// these columns surface the live Poeton ProgressLog feed (job number + qty
// + latest status pill) and are workflow-critical for Cardiff. The earlier
// hide was a misread of which columns were "empty"; the actual offending
// column was the trailing 'View →' link, which is gone now.
// Bumped to 10 to hide Priority and Batch/Serial No by default — confirmed
// empty for every Cardiff Poeton line via the live /api/v1/purchasing/lines/
// endpoint (priority='', batch_serial_no=''). They were 240px combined of
// dashes pushing Ack Status off the right edge of the viewport on standard
// laptop widths. Both still in the column picker for tenants that populate
// them.
// Bumped to 11 to hide Job Status, Job #, Job Qty by default — these
// Poeton-specific progress columns are noisy/empty for most tenants and
// push more relevant columns off-screen. Still toggleable via the column
// picker for tenants running the Poeton ProgressLog feed.
// Bumped to 12 to surface the new DNA-driven "Status" column (processing_stage:
// Docked/WIP/Completed/Shipped/On Hold) on every existing user layout.
// Bumped to 13 to surface Priority by default — DNA now populates it (the
// earlier hide predated DNA priority persistence) and the mapping is fixed
// so 1=highest renders as Critical.
// Bumped to 14 to add the Status column header (processing_stage) and hide
// Ack Status by default — the new DNA-driven Status column supersedes it.
export const DEFAULT_COLUMNS_VERSION = 15;

// CR-4716: Commercial/internal fields restricted to supplier users only.
// Customer users must not see these in the table, column picker, or filters.
export const CUSTOMER_RESTRICTED_COLUMNS = [
  'buyer_id',
  'ppv',
  'otif_overridden',
  'otd_overridden',  // CR-5263
  'contract_unit_price',
  'contract_lot_price',
  'contract_price',
];

// Filter state interface
// CR-3413: Removed dateFrom/dateTo (Delivery From/To), combined status filters
export interface POLinesFilters {
  poNumber: string;
  lineNumber: string;
  customer: string;
  customerSearch: string;  // CR-3413: For searchable customer dropdown
  customerGroup: string;
  supplier: string;
  supplierSearch: string;  // CR-3413: For searchable supplier/site dropdown
  partNumber: string;
  description: string;
  status: string;  // CR-3413: Combined status filter (replaces separate status and acknowledgeStatus)
  oddFrom: string;
  oddTo: string;
  on_hold: string;
  buyerId: string;
  batchSerialNo: string;
  otifOverridden: string;
  nextAction: string;
  ppv: string;
  commodityGroup: string;
  commodityType: string;
}

// CR-3413: Removed dateFrom/dateTo, acknowledgeStatus (now combined into status)
export const DEFAULT_FILTERS: POLinesFilters = {
  poNumber: '',
  lineNumber: '',
  customer: '',
  customerSearch: '',
  customerGroup: '',
  supplier: '',
  supplierSearch: '',
  partNumber: '',
  description: '',
  status: '',
  oddFrom: '',
  oddTo: '',
  on_hold: '',
  buyerId: '',
  batchSerialNo: '',
  otifOverridden: '',
  nextAction: '',
  ppv: '',
  commodityGroup: '',
  commodityType: '',
};

// Status filter options — these mirror the values shown in the visible
// "Status" column (PurchaseOrderLine.processing_stage, the DNA-driven stage),
// so filtering matches what the user reads in the grid. Keep in sync with
// PROCESSING_STAGE_CONFIG below.
export const STATUS_OPTIONS = [
  { value: 'docked', label: 'Docked' },
  { value: 'wip', label: 'WIP' },
  { value: 'completed', label: 'Completed' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Filter options from API
export interface FilterOptions {
  customers: Array<{ id: number; name: string }>;
  suppliers: Array<{ id: number; name: string; group?: number | null }>;
  commodity_groups: Array<{ id: number; name: string }>;
  commodity_types: Array<{ id: number; name: string; commodity_group_id: number }>;
  customer_groups?: Array<{ id: number; name: string }>;
}

// Acknowledge status configuration
export interface StatusConfig {
  label: string;
  className: string;
}

export const ACKNOWLEDGE_STATUS_CONFIG: Record<string, StatusConfig> = {
  new: { label: 'New', className: 'bg-blue-500 text-white' },
  declined: { label: 'Declined', className: 'bg-gray-800 text-white' },
  change_proposed_customer: { label: 'Changed', className: 'bg-amber-500 text-white' },
  change_proposed_supplier: { label: 'Changed', className: 'bg-amber-500 text-white' },
  wip: { label: 'WIP', className: 'bg-green-500 text-white' },
  accepted: { label: 'WIP', className: 'bg-green-500 text-white' },
  despatched: { label: 'Despatched', className: 'bg-gray-200 text-gray-900' },
  change_declined: { label: 'Change Declined', className: 'bg-red-500 text-white' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-600 text-white' },
  closed: { label: 'Closed', className: 'bg-gray-400 text-white' },
};

// DNA-driven processing stage configuration (PurchaseOrderLine.processing_stage).
// Mirrors the DNA commercial Status code (see poeton_po_import.py):
//   Live -> Docked; In Progress / Approved -> WIP; Completed -> Completed;
//   Closed -> Completed (becomes Shipped on ASN submission / historic backfill);
//   Cancelled -> Cancelled. On Hold is the separate on_hold flag overlay.
export const PROCESSING_STAGE_CONFIG: Record<string, StatusConfig> = {
  docked: { label: 'Docked', className: 'bg-blue-500 text-white' },
  wip: { label: 'WIP', className: 'bg-amber-500 text-white' },
  completed: { label: 'Completed', className: 'bg-green-500 text-white' },
  shipped: { label: 'Shipped', className: 'bg-gray-200 text-gray-900' },
  on_hold: { label: 'On Hold', className: 'bg-orange-500 text-white' },
  cancelled: { label: 'Cancelled', className: 'bg-gray-600 text-white' },
};

// Next action result type
export interface NextActionResult {
  text: string;
  className: string;
}

// Re-export PurchaseOrderLine for convenience
export type { PurchaseOrderLine };
